import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  CircularProgress,
  Card,
  CardContent,
  Button, // Added Button
  Chip, // For Assignee
  Grid, // For 2-column properties
  Collapse,
  IconButton
} from '@mui/material';
import { Check, X } from 'lucide-react'; // Added Check and X icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Link as RouterLink } from 'react-router-dom';

const ExtractedInfoPanel = ({
  documentExtractionTasks = [],
  tasksLoading = false,
  tasksError = null,
  onApproveTask = (taskId) => console.log('Approve task:', taskId),
  handleRejectTask = (taskId) => console.log('Reject task:', taskId),
  approvedTasks = new Set(),
  mutatingTaskId = null, // ID of the task currently being updated
  isUpdateTaskLoading = false, // Whether a task update mutation is in progress
}) => {
  const [expandedTasks, setExpandedTasks] = useState({});

  const handleToggleExpand = (taskId) => {
    setExpandedTasks(prev => ({ ...prev, [taskId]: !prev[taskId] }));
  };
  return (
    <Paper elevation={2} sx={{ p: 3, height: '100%', overflowY: 'auto' }}>
      <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
        Extracted Info
      </Typography>

      {/* Document Extraction Tasks Section */}
      <Box>
        {tasksLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 2 }}>
            <CircularProgress size={24} />
            <Typography sx={{ ml: 1 }}>Loading tasks...</Typography>
          </Box>
        )}
        {tasksError && (
          <Typography color="error" sx={{ p: 2 }}>
            Error loading tasks: {tasksError.message}
          </Typography>
        )}
        {!tasksLoading && !tasksError && documentExtractionTasks.length === 0 && (
          <Typography color="text.secondary" sx={{ p: 2, fontStyle: 'italic' }}>
            No document tasks found for the currently displayed documents.
          </Typography>
        )}
        {!tasksLoading && !tasksError && documentExtractionTasks.length > 0 && (
          <List dense>
            {documentExtractionTasks.map((task) => {
              // console.log('Task createdAt value:', task.createdAt, 'Type:', typeof task.createdAt);
              const isApproved = approvedTasks.has(task.id);
              const isExpanded = !!expandedTasks[task.id];
              return (
                <ListItem key={task.id} sx={{ p: 0, mb: 1.5 }} alignItems="flex-start">
                  <Card 
                    variant="outlined" 
                    sx={{
                      width: '100%',
                      backgroundColor: isApproved ? (theme) => theme.palette.success.lighter || 'rgba(46, 125, 50, 0.1)' : 'transparent',
                      borderColor: isApproved ? (theme) => theme.palette.success.main : 'divider',
                      borderWidth: isApproved ? '1px' : '1px', // Ensure border is visible
                      borderStyle: 'solid',
                    }}
                  >
                    <CardContent sx={{ '&:last-child': { pb: isExpanded ? 2 : 1 }, pt: 1.5, px: 2 }}>
                      {/* Top section: Text (left), Assignee (right), and Expand Icon */}
                      <Box 
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          cursor: 'pointer',
                          mb: isExpanded ? 1 : 0 // No margin bottom if collapsed
                        }}
                        onClick={() => handleToggleExpand(task.id)}
                      >
                        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', overflow: 'hidden', mr: 1 }}>
                          {task.Text ? (
                            <Typography variant="body1" noWrap gutterBottom sx={{ my: 0, flexGrow: 1, wordBreak: 'break-word' }}>
                              {task.Text}
                            </Typography>
                          ) : (
                            <Box sx={{ flexGrow: 1, minHeight: '24px' }} /> // Placeholder if no text
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          {task.Assignee && (
                            <Chip 
                              label={task.Assignee} 
                              size="small" 
                              sx={{ backgroundColor: 'purple', color: 'white', mr: 0.5 }}
                            />
                          )}
                          <IconButton size="small" aria-label={isExpanded ? 'collapse' : 'expand'}>
                            <ExpandMoreIcon sx={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                          </IconButton>
                        </Box>
                      </Box>

                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        {task.document && task.document.name && (
                          <Box sx={{ pt: 1, px:0, mb: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                            <Typography variant='caption' sx={{ fontWeight: 'bold', fontSize: '0.8rem', color: 'text.secondary' }}>
                              Source:
                            </Typography>
                            <Typography variant='caption' sx={{ fontSize: '0.75rem', color: 'text.primary', pl: 1, wordBreak: 'break-word' }}>
                              {task.document.name}
                            </Typography>
                          </Box>
                        )}
                        {/* Middle section: Properties, CreatedAt */}
                        <Box sx={{ mb: 1 }}> {/* Removed pt:1 as source is now above with its own padding */}

                        {task.Properties && typeof task.Properties === 'object' && Object.keys(task.Properties).length > 0 && (
                          <Box mt={1}>
                            <Grid container spacing={0.5}>
                              {Object.entries(task.Properties).map(([key, value]) => (
                                <Grid item xs={12} key={key}>
                                  <ListItem sx={{ pl: 0, py: 0.1, display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                    <Typography variant='caption' sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>{`${key}:`}</Typography>
                                    <Typography variant='caption' sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', pl:1, fontSize: '0.75rem' }}>
                                      {typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)}
                                    </Typography>
                                  </ListItem>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        )}

                        {/* Dependents Section */}
                        {task.Dependents && Array.isArray(task.Dependents) && task.Dependents.length > 0 && (
                          <Box mt={1.5}>
                            <Typography variant='caption' sx={{ fontWeight: 'medium', display: 'block', mb: 0.5, fontSize: '0.8rem' }}>
                              This will effect:
                            </Typography>
                            <List dense sx={{ pl: 0, pt: 0 }}>
                              {task.Dependents.map((dep, index) => (
                                <ListItem key={index} sx={{ pl: 0, py: 0.25 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                    <WarningAmberIcon sx={{ color: 'warning.main', fontSize: '1rem', mr: 0.5 }} />
                                    {isApproved ? (
                                      <RouterLink 
                                        to="/Documents/Detail/b63eecf9-252c-4f8c-a65c-bea263320fc9"
                                        style={{ textDecoration: 'none' }} // RouterLink itself doesn't need underline, Typography will handle
                                      >
                                        <Typography 
                                          variant="caption" 
                                          sx={{ 
                                            fontSize: '0.75rem', 
                                            textDecoration: 'underline', 
                                            color: 'primary.main',
                                            '&:hover': {
                                              textDecoration: 'underline', // Ensure underline on hover too
                                            }
                                          }}
                                        >
                                          {typeof dep === 'string' ? dep : (dep.name || dep.id || JSON.stringify(dep))}
                                        </Typography>
                                      </RouterLink>
                                    ) : (
                                      <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                        {typeof dep === 'string' ? dep : (dep.name || dep.id || JSON.stringify(dep))}
                                      </Typography>
                                    )}
                                  </Box>
                                </ListItem>
                              ))}
                            </List>
                          </Box>
                        )}
                        
                        {task.createdAt && !isNaN(new Date(task.createdAt).getTime()) ? (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: 'left', fontSize: '0.7rem' }}>
                            Created: {new Date(task.createdAt).toLocaleString()}
                          </Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1, textAlign: 'left', fontSize: '0.7rem' }}>
                            Created: Date not available
                          </Typography>
                        )}
                        </Box>

                        {/* Bottom section: Approve/Reject Button */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 1 }}>
                        {isApproved ? (
                          <Button 
                            size="small" 
                            startIcon={<X size={14} />} 
                            variant="outlined"
                            color="error"
                            onClick={() => handleRejectTask(task.id)} 
                            disabled={isUpdateTaskLoading && mutatingTaskId === task.id}
                            sx={{ fontSize: '0.75rem', py: 0.2, px: 1 }} 
                          >
                            Reject
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            startIcon={<Check size={14} />}
                            onClick={() => onApproveTask(task.id)}
                            color="success"
                            variant="outlined"
                            disabled={isUpdateTaskLoading && mutatingTaskId === task.id}
                            sx={{ 
                              minWidth: 'auto', 
                              fontSize: '0.75rem', 
                              py: 0.2, 
                              px: 1,
                            }}
                          >
                            Approve
                          </Button>
                        )}
                        </Box>
                      </Collapse>
                    </CardContent>
                  </Card>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Paper>
  );
};

export default ExtractedInfoPanel;
